import { createElement, Fragment, PropsWithChildren } from "react";
import { SearchState } from "./types";
import { EntityPreview } from "./components/EntityPreview";
import { SubQueryPreview } from "./components/SubQueryPreview";
import { useAggregations } from "./lib/useAggregations";
import { useEntitiesList } from "./lib/useEntitiesList";
import { useEntityPreview } from "./lib/useEntityPreview";
import { useInputParams } from "./lib/useInputParams";
import { useSubQueryPreview } from "./lib/useSubQueryPreview";
import { useEventSummary } from "./lib/useEventSummary";
import { useTimeSeries } from "./lib/useTimeSeries";
import { useCheckpoint } from "./lib/useCheckpoint";
import { createStateReducer } from "./lib/params-state-reducer";
import { useApplicationContext } from "src/hooks/useApplication";

/**
 * The search context drives the search-related features of the application.
 *
 * The search context doesn't do anything automatically. Instead, it
 * provides mutators that can be called as-needed. The gives the control
 * to the caller to order and prioritize the API calls.
 * */
export function useSearch(
  initial: SearchState | undefined,
  ...merge: Parameters<typeof createStateReducer>
): SearchContext {
  const { org_id: orgId } = useApplicationContext();
  if (!orgId)
    throw new Error("Expected authed session for an org (missing orgId)");

  /**
   * The primary user input driving the application page
   **/
  const params = useInputParams({ ...initial, orgId }, ...merge);

  /**
   * The subscriptions available to the caller for using the search
   **/
  const aggregations = useAggregations(params);
  const eventsList = useEntitiesList(params);
  const entitiesList = useEntitiesList(params);
  const summary = useEventSummary(params);
  const timeSeries = useTimeSeries(params);

  /**
   * Other search-specific helper features?
   **/
  const checkpoint = useCheckpoint({ aggregations, eventsList, entitiesList });
  const entity = useEntityPreview();
  const subquery = useSubQueryPreview();
  return {
    aggregations,
    checkpoint,
    entitiesList,
    entity,
    eventsList,
    merge,
    params,
    subquery,
    summary,
    timeSeries,
  };
}

/**
 * SearchContext and searchContext are used for provider integration */
export type SearchContext = {
  aggregations: ReturnType<typeof useAggregations>;
  checkpoint: ReturnType<typeof useCheckpoint>;
  entitiesList: ReturnType<typeof useEntitiesList>;
  entity: ReturnType<typeof useEntityPreview>;
  eventsList: ReturnType<typeof useEntitiesList>;
  merge: Parameters<typeof createStateReducer>;
  params: ReturnType<typeof useInputParams>;
  subquery: ReturnType<typeof useSubQueryPreview>;
  summary: ReturnType<typeof useEventSummary>;
  timeSeries: ReturnType<typeof useTimeSeries>;
};

export function SearchDependencies({
  children,
  context,
}: PropsWithChildren<{ context: SearchContext }>) {
  const entityPreview = createElement(EntityPreview, {
    key: "entity-preview",
    context,
  });
  const subQueryPreview = createElement(SubQueryPreview, {
    key: "sub-query-preview",
    context,
  });
  return createElement(Fragment, {}, [
    children,
    entityPreview,
    subQueryPreview,
  ]);
}
